import { useEffect, useState } from 'react';
import { ReactEditor, useFocused, useSlateSelection, useSlateStatic } from 'slate-react';
import { useThrottledCallback } from 'use-debounce';

/**
 * Calculate the coordinates of the selection boundary relative to the "editor" root element
 * @returns {DOMRect|null} - the coordinates of the selection boundaries
 */
export const useSlateContentSelectionRect = (lineHeight) => {
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const focused = useFocused();

  const [selectionRect, setSelectionRect] = useState(null);

  const calculateSelectionRect = useThrottledCallback(({ editor, focused, selection }) => {
    const currentSelection = focused ? selection : editor.getPreviousSelection();
    const range = convertToDomRange(editor, currentSelection);
    if (!range) {
      return;
    }

    const editableElement = ReactEditor.toDOMNode(editor, editor);
    const editableRect = editableElement.getBoundingClientRect();
    const selectionRect = emptyLineRect(lineHeight) || range.getBoundingClientRect();

    // Adjust the coordinates of the selection boundary to be relative to the "rootRef" element
    setSelectionRect(
      new DOMRect(
        selectionRect.x - editableRect.x,
        selectionRect.y - editableRect.y,
        selectionRect.width,
        selectionRect.height
      )
    );
  }, 50);

  useEffect(() => {
    calculateSelectionRect({ editor, focused, selection });
  }, [editor, focused, selection, calculateSelectionRect]);

  // Cancel the debounced callback when the component unmounts
  useEffect(() => {
    return () => {
      calculateSelectionRect.cancel();
    };
  }, [calculateSelectionRect]);

  return selectionRect;
};

const convertToDomRange = (editor, slateRange) => {
  try {
    if (!slateRange) {
      return null;
    }

    if (!ReactEditor.hasRange(editor, slateRange)) {
      return null;
    }

    return ReactEditor.toDOMRange(editor, slateRange);
  } catch {
    return null;
  }
};

const emptyLineRect = (lineHeight) => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) {
    return false;
  }
  const getRange = selection.getRangeAt(0);
  const prevChar = getRange.startContainer.textContent.charAt(getRange.startOffset - 1);
  const curChar = getRange.startContainer.textContent.charAt(getRange.startOffset);
  const isEmptyLine = prevChar === '\n' && curChar === '\n';
  if (isEmptyLine) {
    const rect = getRange.getBoundingClientRect();
    return {
      x: rect.x,
      y: rect.y + lineHeight,
      width: rect.width,
      height: rect.height,
    };
  }
};
